import {
  DownOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Collapse, Checkbox, Button, Spin, DatePicker, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";

function formatString(str) {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

const SessionSidebar = ({
  sessions = [],
  pastSummary,
  currentSessionId,
  selectedSessionIds = [],
  onChangeSelectedSessions,
  onViewPostSummary,
  loadingPastSummary = false,
  sessionsLoading,
  dateRange,
  onChangeDateRange,
  onSearch,
  canViewPastSummary,
  onPrimaryPastSummaryAction,
}) => {
  const categories = useMemo(
    () => [...new Set(sessions.map((s) => s.category || "Other"))],
    [sessions]
  );

  const [activeKeys, setActiveKeys] = useState([]);
  useEffect(() => {
    setActiveKeys(categories);
  }, [categories]);

  const allIds = useMemo(() => sessions.map((s) => String(s.id)), [sessions]);

  // Derived flags to keep deps simple & correct
  const isEmptyAfterLoad = useMemo(
    () => !sessionsLoading && sessions.length === 0,
    [sessionsLoading, sessions.length]
  );
  const selectedCount = selectedSessionIds.length;

  // Guard 1: If API failed (sessions are empty after load), clear selections → "0/0"
  useEffect(() => {
    if (isEmptyAfterLoad && selectedCount > 0) {
      onChangeSelectedSessions?.([]);
    }
  }, [isEmptyAfterLoad, selectedCount, onChangeSelectedSessions]);

  // Guard 2: If sessions changed, prune selections not present anymore
  useEffect(() => {
    if (selectedSessionIds.length === 0) return;

    const allowed = new Set(allIds);
    const pruned = selectedSessionIds.filter((id) => allowed.has(String(id)));

    if (pruned.length !== selectedSessionIds.length) {
      onChangeSelectedSessions?.(pruned);
    }
  }, [allIds, selectedSessionIds, onChangeSelectedSessions]);

  const isSelected = (id) =>
    selectedSessionIds.includes(String(id)) ||
    selectedSessionIds.includes(Number(id));

  const toggleOne = (id, checked) => {
    const sid = String(id);
    if (checked) {
      const next = Array.from(new Set([...selectedSessionIds, sid]));
      onChangeSelectedSessions?.(next);
    } else {
      onChangeSelectedSessions?.(
        selectedSessionIds.filter((x) => String(x) !== sid)
      );
    }
  };

  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedSessionIds.includes(id));

  const toggleAll = (checked) => {
    onChangeSelectedSessions?.(checked ? allIds : []);
  };

  const hasSelection = selectedSessionIds.length > 0;
  const hasRange = !!(dateRange?.[0] || dateRange?.[1]);

  return (
    <div
      style={{
        position: "relative",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
      }}
      className="bg-white w-[300px] overflow-y-auto p-6 pt-6 rounded-xl border border-gray-100 flex flex-col gap-y-4"
      aria-busy={loadingPastSummary && hasSelection}
    >
      {/* Header */}
      <div
        className="flex justify-between items-center"
        style={{
          paddingBottom: "8px",
          margin: "0px -20px 20px",
          borderBottom: "2.5px solid rgb(226, 232, 240)",
        }}
      >
        <div className="flex flex-col justify-center items-start gap-3 w-full">
          <h3 className="text-gray-800 font-semibold text-[15px] tracking-wide uppercase letter-spacing-0.5px m-0 px-5">
            <UnorderedListOutlined className="mr-4" />
            Past Sessions
          </h3>
          {hasSelection && loadingPastSummary && (
            <div className="flex justify-center items-center mt-4 gap-2 mx-auto">
              <Spin
                tip="Loaing past sessions..."
                size="small"
                style={{ color: "#1677ff" }}
              />
              <p className="text-gray-500 text-sm font-medium">
                Generating past summary...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex items-center gap-2">
        <DatePicker.RangePicker
          value={dateRange}
          onChange={(vals) => onChangeDateRange?.(vals || [null, null])}
          allowClear
          disabled={sessionsLoading}
          style={{ width: "100%" }}
          format="MM/DD/YY"
          placeholder={["Start date", "End date"]}
          disabledDate={(current) => current && current > new Date()}
        />
        <Tooltip title="Search">
          <Button
            icon={<SearchOutlined />}
            onClick={onSearch}
            disabled={sessionsLoading || !hasRange}
            type="primary"
            shape="circle"
          />
        </Tooltip>
      </div>

      {/* Select All Controls */}
      <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg border border-gray-200">
        <Checkbox
          checked={allSelected}
          onChange={(e) => toggleAll(e.target.checked)}
          style={{ fontSize: "14px", fontWeight: "500" }}
          className="text-gray-700"
        >
          <span className="text-gray-700 font-medium">Select all</span>
        </Checkbox>
        <span className="text-sm font-medium text-gray-500 bg-white px-2 py-1 rounded-md border">
          {selectedSessionIds.length}/{allIds.length}
        </span>
      </div>

      {/* Sessions List */}
      {sessionsLoading ? (
        <div
          style={{
            height: "300px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin tip="Loading sessions..." size="large" />
          <p className="mt-4 text-gray-500 text-sm font-medium">
            Loading sessions...
          </p>
        </div>
      ) : (
        <div>
          {hasSelection && (
            <div className="flex gap-2 flex-col">
              <div className="border-t border-gray-100">
                <CustomButton
                  loading={loadingPastSummary}
                  loadingText="Generating Past Summary..."
                  text={
                    canViewPastSummary
                      ? "View Past Summary"
                      : "Generate Past Summary"
                  }
                  onClick={onPrimaryPastSummaryAction}
                  icon={
                    canViewPastSummary ? (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    )
                  }
                />
              </div>
            </div>
          )}
          <div className="flex-1">
            <Collapse
              expandIcon={({ isActive }) => (
                <DownOutlined
                  rotate={isActive ? 180 : 0}
                  style={{ color: "#4b5563", fontSize: "12px" }}
                />
              )}
              className="border-none bg-transparent"
              expandIconPosition="end"
              ghost
              activeKey={activeKeys}
              onChange={(keys) => setActiveKeys(keys)}
              style={{ backgroundColor: "transparent", height: "100%" }}
            >
              {categories.map((category) => (
                <Collapse.Panel
                  key={category}
                  header={
                    <div className="font-semibold text-gray-800 text-[15px] py-1">
                      {category}
                    </div>
                  }
                  className="session-sidebar-panel"
                  style={{
                    border: "none",
                    backgroundColor: "transparent",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    className="space-y-2 pt-2 pb-2"
                    style={{ maxHeight: "calc(100vh - 40rem)" }}
                  >
                    {console.log({ sessions })}
                    {sessions
                      .filter(
                        (session) => (session.category || "Other") === category
                      )
                      .map((session) => {
                        const checked = isSelected(session.id);
                        const isActive = session.id === currentSessionId;
                        return (
                          <div
                            key={session.id}
                            className={`relative rounded-lg border transition-all duration-200 cursor-pointer ${
                              checked
                                ? "border-gray-200 bg-gray-50"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                            } ${isActive ? "border-gray-500" : ""}`}
                          >
                            <div className="p-3">
                              <div className="flex items-start gap-2">
                                <Checkbox
                                  checked={checked}
                                  onChange={(e) =>
                                    toggleOne(session.id, e.target.checked)
                                  }
                                  disabled={loadingPastSummary}
                                  style={{ marginTop: "2px" }}
                                />

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="font-semibold text-sm text-gray-900">
                                      {session.clinician_name}
                                    </div>
                                    {isActive && (
                                      <span className="text-xs text-gray-500">
                                        • Current
                                      </span>
                                    )}
                                  </div>

                                  <div className="font-medium text-sm text-gray-600 mb-1">
                                    {formatString(session.session_type || "")}
                                  </div>

                                  <div className="text-xs text-gray-500">
                                    <span className="font-medium">
                                      {session.date}
                                    </span>
                                    <span className="mx-2">•</span>
                                    <span className="font-medium">
                                      {session.time}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
          </div>
        </div>
      )}

      {/* Footer branding (POWERED BY MAX.AI) removed for the AIspire demo build */}
    </div>
  );
};
const sizeClassMap = {
  small: "h-9 px-3 text-[13px]",
  middle: "h-11 px-4 text-[14px]",
  large: "h-12 px-5 text-[15px]",
};

export const CustomButton = ({
  loading = false,
  loadingText = "Loading...",
  text,
  icon,
  children,
  onClick,
  disabled,
  block = true,
  size = "large",
  className = "",
  style,
  ...rest
}) => {
  const isDisabled = disabled || loading;

  const mergedStyle = {
    borderRadius: 8,
    ...style,
  };

  const classes = useMemo(() => {
    return [
      "inline-flex items-center justify-center gap-2 w-full",
      block ? "w-full" : "w-auto",
      sizeClassMap[size] ?? sizeClassMap.large,
      "rounded-lg border transition-all duration-200 select-none",
      "bg-white border-slate-200 text-[#1677ff] shadow-none",
      !isDisabled &&
        "hover:bg-[#f0f7ff] hover:border-slate-300 active:scale-[0.98]",
      !isDisabled &&
        "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#bae0ff]",
      isDisabled &&
        "text-slate-400 bg-white border-slate-200 cursor-not-allowed opacity-60",
      className,
    ]
      .filter(Boolean)
      .join(" ");
  }, [block, size, isDisabled, className]);

  return (
    <Button
      {...rest}
      type="default"
      size={size}
      block={block}
      loading={loading}
      onClick={onClick}
      disabled={isDisabled}
      style={mergedStyle}
      className={classes}
    >
      <div className="flex items-center justify-center gap-2">
        {loading ? (
          <span>{loadingText}</span>
        ) : (
          <>
            {icon ? <span className="inline-flex">{icon}</span> : null}
            <span>{text ?? children}</span>
          </>
        )}
      </div>
    </Button>
  );
};

export default SessionSidebar;
